import Promotion, { ModelType } from "./model";
import normalizedActions from "../utils/normalized/actions";
import fetchInternal from "../../utils/fetch";
import siteStore from "../../services/siteStore";
import { UPDATE_BEST_ELIGIBLE_PROMOTION_ID } from "./reducer";
import fetch from "../utils/normalized/fetch";
import PromotionPlan from "../promotion-plan/model";
import { addPromotionPlan } from "../promotion-plan/actions";
import { captureError } from "../../utils/error";

const DEFAULT_FETCH_CONFIG = {
  credentials: "include",
  headers: {
    "Content-Type": "application/json",
  },
};

const createPromotionFromData = (data) => {
  // Validate date ranges if both dates are present
  if (data.attributes) {
    const activatesAt = new Date(data.attributes.activatesAt);
    const expiresAt = new Date(data.attributes.expiresAt);

    if (
      !isNaN(activatesAt.getTime()) &&
      !isNaN(expiresAt.getTime()) &&
      activatesAt > expiresAt
    ) {
      return null;
    }
  }

  const promotion = new Promotion();
  promotion.deserialize(data);
  return promotion;
};

const handlePromotionPlans = (included = [], dispatch) => {
  included.forEach((data) => {
    if (data.type === "promotion_plans") {
      const model = new PromotionPlan();
      model.deserialize(data);
      dispatch(addPromotionPlan(model));
    }
  });
};

const updateBestEligiblePromotionId = (dispatch, promotionId) => {
  dispatch({
    type: UPDATE_BEST_ELIGIBLE_PROMOTION_ID,
    payload: { bestEligiblePromotionId: promotionId },
  });
};

export function addPromotionsFromGraphQL(promotionData) {
  return (dispatch) => {
    promotionData.forEach((data) => {
      const promotion = createPromotionFromData(data);
      dispatch(addPromotion(promotion));
    });
  };
}

export function removePromotion(id) {
  return (dispatch) => {
    return normalizedActions.removeModel(dispatch, ModelType, id);
  };
}

export function addPromotionFromNormalizedData(data, associations) {
  return (dispatch) => {
    const promotion = createPromotionFromData(data);
    dispatch(addPromotion(promotion, associations));
  };
}

export function addPromotion(data, associations) {
  return (dispatch, getState) => {
    return normalizedActions.updateOrCreateModel(
      dispatch,
      getState().promotion,
      ModelType,
      data,
      associations,
    );
  };
}

export function refreshBestEligiblePromotion() {
  return async (dispatch, getState) => {
    try {
      // Check if we have a Fermat discount code applied - if so, don't override it
      const fermatDiscountApplied = sessionStorage.getItem('fermat_discount_applied');
      if (fermatDiscountApplied) {
        return;
      }

      const params = new URLSearchParams({
        store_id: siteStore.storeId,
      }).toString();

      const response = await fetchInternal(
        `promotions/best_eligible?${params}`,
        {
          ...DEFAULT_FETCH_CONFIG,
          method: "GET",
        },
      );

      if (!response) {
        updateBestEligiblePromotionId(dispatch, undefined);
        return;
      }

      const promotion = createPromotionFromData(response.data);

      if (!promotion) {
        updateBestEligiblePromotionId(dispatch, undefined);
        return;
      }

      // Update best eligible promotion ID
      updateBestEligiblePromotionId(dispatch, promotion.id);

      // Handle any included promotion plans
      handlePromotionPlans(response.included, dispatch);

      // Handle included promotions
      if (response.included) {
        const includedPromotions = response.included
          .filter((item) => item.type === "promotions")
          .map((data) => createPromotionFromData(data))
          .filter((p) => p !== null);

        for (const includedPromotion of includedPromotions) {
          await normalizedActions.updateOrCreateModel(
            dispatch,
            getState().promotion,
            ModelType,
            includedPromotion,
          );
        }
      }

      // Update the promotion model in store
      return normalizedActions.updateOrCreateModel(
        dispatch,
        getState().promotion,
        ModelType,
        promotion,
      );
    } catch (error) {
      updateBestEligiblePromotionId(dispatch, undefined);
      captureError(error, "Error in promotion action");
      return undefined;
    }
  };
}

export function fetchPromotions() {
  return async (dispatch) => {
    try {
      return await fetch(
        `promotions/active`,
        {
          ...DEFAULT_FETCH_CONFIG,
          mode: "cors",
        },
        dispatch,
        ModelType,
      );
    } catch (error) {
      captureError(error, "Error in promotion action");
      return undefined;
    }
  };
}
